import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { CardBody } from 'reactstrap';

import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import { modelOf } from '../../../../prop-types';
import AnalyticsService from '../../../../services/AnalyticsService';
import CheckoutStore from '../../../../store/CheckoutStore';
import CheckoutPageModule from '../../../../types/checkout/CheckoutPageModule';
import FormFieldType from '../../../../types/FormFieldType';
import OrderPaymentProvider from '../../../../types/order/OrderPaymentProvider';
import RequestState from '../../../../types/RequestState';
import { createOption } from '../../../form/Field';
import CheckoutMethodSelector from '../../common/CheckoutMethodSelector';
import CheckoutModule from '../../common/CheckoutModule';
import ConstrainedImage from '../../../common/ConstrainedImage';

const CheckoutPaymentProviderModule = ({
  analyticsService,
  checkoutStore,
  onOrderUpdate,
  orderNumber,
}) => {
  const { order, paymentMethodIsLoading, state } = checkoutStore;
  const { selected_payment, updateSelectedPayment, payment_providers } = order;

  const disabled = paymentMethodIsLoading || state === RequestState.LOADING;

  const form = useMemo(() => {
    const paymentProviderFormFields = {
      paymentProvider: createFormField({
        type: FormFieldType.BOOLEAN,
        value: selected_payment.provider || null,
        required: true,
      }),
    };

    return createForm(paymentProviderFormFields);
  }, [selected_payment.provider]);

  const handleUpdate = (option) => {
    const isExternal = option.value === OrderPaymentProvider.KUSTOM;
    updateSelectedPayment({
      paymentProviderId: option.value,
      paymentMethodGroupId: isExternal ? null : undefined,
      paymentMethodId: isExternal ? null : undefined,
    });
    onOrderUpdate({
      module: CheckoutPageModule.PAYMENT_PROVIDER_MODULE,
      withoutTimer: true,
    });

    // Send analytics when payment provider changes (store has new provider before this runs)
    if (order.productsTotal?.value != null) {
      analyticsService.addPaymentInfo({
        products: order.productList,
        productsTotal: order.productsTotal.value,
      });
    }
  };

  const createOptions = () => {
    return payment_providers.map((provider) =>
      createOption({
        value: provider.id,
        label: (
          <div
            key={provider.id}
            className="CheckoutPaymentProviderModule__selector"
          >
            {provider.title && (
              <span className="CheckoutPaymentProviderModule__selector-title">
                {provider.title}
              </span>
            )}
            {provider.icon && (
              <div className="CheckoutPaymentProviderModule__selector-image-row">
                <ConstrainedImage
                  src={provider.icon}
                  alt={provider.description || provider.title}
                  className="CheckoutPaymentProviderModule__selector-image-wrap"
                  defaultHeight={40}
                  constrainedHeight
                />
              </div>
            )}
            {provider.description ? (
              <span className="CheckoutPaymentProviderModule__selector-description">
                {provider.description}
              </span>
            ) : null}
          </div>
        ),
      })
    );
  };

  return (
    <CheckoutModule
      className="CheckoutPaymentProviderModule"
      generalAlerts={[]}
      errorAlerts={[]}
      title={
        <FormattedMessage
          id="checkout.paymentProvider.title"
          defaultMessage="Select payment provider"
        />
      }
      orderNumber={orderNumber}
      module={CheckoutPageModule.PAYMENT_PROVIDER_MODULE}
    >
      <CardBody className="CheckoutPaymentProviderModule__content">
        <CheckoutMethodSelector
          form={form}
          field="paymentProvider"
          fieldName="paymentProvider"
          disabled={disabled}
          options={createOptions()}
          onChange={handleUpdate}
        />
      </CardBody>
    </CheckoutModule>
  );
};

CheckoutPaymentProviderModule.propTypes = {
  analyticsService: PropTypes.instanceOf(AnalyticsService).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  orderNumber: PropTypes.number.isRequired,
};

export default inject(
  'analyticsService',
  'checkoutStore'
)(observer(CheckoutPaymentProviderModule));
